import type { Inline } from './types.ts'

/**
 * Alternation order carries the two ambiguities of the format, and reordering it
 * breaks them silently: a backtick span matches first so `**` inside code stays
 * literal, and `**strong**` is tried before `*em*` so the opening pair is never
 * read as an empty emphasis.
 */
const INLINE_SOURCE = String.raw`(\x60+)([\s\S]*?)\1|\*\*([\s\S]+?)\*\*|\*([^*\n]+?)\*|\[([^\]]*)\]\(([^)\s]+)\)|(https?://[^\s<>()[\]]+)`

/**
 * The text comes from an external system, so `javascript:` and `data:` are
 * reachable input, not hypotheticals. Rejected targets stay visible as text.
 */
function safeHref(href: string): string | null {
  try {
    const { protocol } = new URL(href, 'https://example.invalid')
    return protocol === 'https:' || protocol === 'http:' ? href : null
  } catch {
    return null
  }
}

function push(out: Inline[], value: string): void {
  if (value === '') return
  const last = out[out.length - 1]
  // Fused, because a rejected match is pushed back as text and has to rejoin
  // the run around it — otherwise one word arrives split into three nodes.
  if (last?.kind === 'text') last.value += value
  else out.push({ kind: 'text', value })
}

export function parseInline(src: string): Inline[] {
  const out: Inline[] = []
  let last = 0
  // Built per call, not shared: this function recurses into the children of a
  // bold or a link, and a module-level global regex would have its `lastIndex`
  // reset by the inner walk — leaving the outer loop to restart forever.
  const scan = new RegExp(INLINE_SOURCE, 'g')

  for (let m = scan.exec(src); m !== null; m = scan.exec(src)) {
    push(out, src.slice(last, m.index))
    last = m.index + m[0].length

    const [, , code, strong, em, linkText, linkHref, bare] = m

    if (code !== undefined) out.push({ kind: 'code', value: code.trim() })
    else if (strong !== undefined) out.push({ kind: 'strong', children: parseInline(strong) })
    else if (em !== undefined) out.push({ kind: 'em', children: parseInline(em) })
    else if (linkHref !== undefined) {
      const href = safeHref(linkHref)
      if (href === null) push(out, m[0])
      else out.push({ kind: 'link', href, children: parseInline(linkText ?? '') })
    } else if (bare !== undefined) {
      const href = safeHref(bare)
      if (href === null) push(out, bare)
      else out.push({ kind: 'link', href, children: [{ kind: 'text', value: bare }] })
    }
  }

  push(out, src.slice(last))
  return out
}
